import { useState } from 'react'
import { Table, Button, Spacer, Flex } from '@jisr-hr/design-system'
import { Typography } from '@jisr-hr/ds'
import { ReactComponent as CrossIcon } from '@jisr-hr/design-system/dist/assets/icons/Icons12px/Cross.svg'
import { ReactComponent as PlusIcon } from '@jisr-hr/design-system/dist/assets/icons/plus-icon.svg'
import { titleCase } from 'utils/string'
import JisrLoader from 'components/global/JisrLoader'
import { EmployeeJobIdAvatar } from 'components/global/molecules'
import { useSelector, useDispatch } from 'utils/hooks'
import StepTemplate from 'containers/public/Onboarding/components/StepTemplate'
import { removeAdminUser } from 'redux/organizationOnborading/roles/actionCreators'
import { admenEmployeesLoad } from 'redux/organizationOnborading/employees/actionCreators'
import I18n from 'translations/i18n'
import { organizationInfo } from 'redux/organizationOnborading/organization/actionCreators'
import { labels } from 'utils/whitelabel'
import { useTrackingSteps } from 'containers/public/Onboarding/helper'
import { StepsTypes } from 'containers/public/Onboarding/types/onboardingTypes'

import Styles from './style.module.css'
import AddUsers from './AddUsers'

const AdminUser = ({
  goNextStep,
  goPreviousStep,
  nextStep,
  previousStep,
}: StepsTypes): JSX.Element => {
  const dispatch = useDispatch()

  const { adminEmployees, fetching } = useSelector((s) => s.reviewingEmployee)
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)

  const AdminUsers = adminEmployees?.filter((user) =>
    user?.roles.find((role) => role?.is_super_admin),
  )

  const onRemoveUser = (id: number): void => {
    dispatch(
      removeAdminUser({
        employee_role: [{ employee_id: id }],
      }),
    ).then(() => {
      dispatch(admenEmployeesLoad())
      dispatch(organizationInfo())
    })
  }

  const handleNext = (): void => {
    goNextStep?.()
  }

  useTrackingSteps({ step_name: 'invite_employees', level_name: 'admin_users' })

  return (
    <StepTemplate
      goNextStep={handleNext}
      goPreviousStep={goPreviousStep}
      nextStep={nextStep}
      previousStep={previousStep}
      fetching={fetching}
    >
      {fetching && <JisrLoader absolute />}
      <Typography
        variant="heading"
        text={I18n.t('admin_users')}
      />

      <Spacer height={8} />
      <Typography
        variant="subtitle-1"
        style={{ paddingInlineEnd: '70px', color: 'var(--color-base-colors-black-500)' }}
        text={I18n.t('admin_users_desc', { app_name: labels.translation_name })}
      />

      <Flex
        justifyEnd
        style={{ paddingInlineEnd: '70px' }}
      >
        <Button
          label={I18n.t('add_admins_onb')}
          size="medium"
          onClick={(): void => setIsFormOpen(true)}
          startIcon={<PlusIcon className={Styles.plusIcon} />}
        />
      </Flex>
      <AddUsers
        open={isFormOpen}
        onClose={(): void => {
          setIsFormOpen(false)
          dispatch(admenEmployeesLoad())
        }}
      />

      <Spacer height={32} />
      <Table
        className={Styles.userTable}
        withActions
        disableHoverStyles
        withColumnBorder={false}
        containerStyles={{ height: 'calc(100vh - 312px)', overflowY: 'auto' }}
        headers={[
          {
            key: 'user',
            label: titleCase(I18n.t('user')),
          },
          {
            key: '',
            label: '',
          },
        ].filter(Boolean)}
      >
        {AdminUsers?.map((employee, i: number) => {
          return (
            <Table.Row key={i}>
              <Table.Cell>
                {/* @ts-expect-error Need to migrate EmployeeJobIdAvatar.js to TypeScript */}
                <EmployeeJobIdAvatar
                  id={employee.code}
                  name={employee.full_name_i18n}
                  avatar={employee.avatar_thumb}
                  jobTitle={employee.job_title?.name_i18n}
                />
              </Table.Cell>
              <Table.Cell>
                <Flex justifyEnd>
                  <Button
                    variant="secondary"
                    size="small"
                    icon={<CrossIcon className={Styles.crossIcon} />}
                    onClick={(): void => onRemoveUser(employee?.id)}
                  />
                </Flex>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table>
    </StepTemplate>
  )
}

export default AdminUser
